'use client';

import { c as _c } from "react/compiler-runtime";
import { type UserPortfolioPositionWire, useActiveWallet, useClaimBackerFees, useClaimPrize, useClaimReceipt, useGetBackerRewardsField, useTurnkeySigningDeps } from '@morfi/backend-sdk-v2/client';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useEffect } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { DataListItem, type DataListItemMetric } from '@/components/ui/data-list-item';
import { StatusPill } from '@/components/ui/status-pill';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useFormattedCurrency } from '@/lib/currency';
import { formatBackerVaultShare } from '@/lib/format/backer-vault-share';
import { logger } from '@/lib/telemetry/logger';
import { cn } from '@/lib/ui/utils';
import { isPositiveRewardAmount } from './portfolio-reward-amount';
import type { RewardView } from './portfolio-rewards';
interface RewardItem {
  market: string;
  wallet: string | null;
  kind: 'prize' | 'backer';
  amountD6: string;
  burnD6: string;
  href: string;
  primaryText: string;
  secondaryText?: ReactNode;
  image: ReactNode;
  imageBadge?: ReactNode;
}
type ClaimMutation = Pick<ReturnType<typeof useClaimPrize>, 'data' | 'variables' | 'isPending' | 'mutate'>;
export function PortfolioPrizeRewardItem(t0) {
  const $ = _c(30);
  const {
    row,
    view,
    own,
    fresh
  } = t0;
  const t1 = row.outcomeLabel ?? "";
  let t2;
  if ($[0] !== row.outcomeIndex || $[1] !== t1) {
    t2 = {
      title: t1,
      index: row.outcomeIndex
    };
    $[0] = row.outcomeIndex;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== row.market) {
    t3 = {
      marketPubkey: row.market
    };
    $[3] = row.market;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const color = useOutcomeColor(t2, t3);
  const t4 = row.market;
  const t5 = row.wallet;
  const t6 = view === "claimable" ? row.prizeClaimableD6 : row.prizeClaimedD6;
  const t7 = row.claimRightsBalanceD6;
  const t8 = row.positionType === "winner" ? `/winners/${row.outcomeMint}` : `/markets/${row.market}`;
  const t9 = row.outcomeLabel ?? row.marketTitle ?? row.market;
  const t10 = row.marketTitle ?? row.market;
  let t11;
  if ($[5] !== color.hue || $[6] !== row.market || $[7] !== row.outcomeImageNsfw || $[8] !== row.outcomeImageUrl || $[9] !== row.outcomeIndex) {
    t11 = <OptimizedImage variant="thumbnail" entity={{
      kind: "outcome",
      marketPubkey: row.market,
      outcomeIndex: row.outcomeIndex,
      hue: color.hue,
      imageUrl: row.outcomeImageUrl,
      nsfw: row.outcomeImageNsfw
    }} alt="" size="size-12" rounded="rounded-control" preview={false} />;
    $[5] = color.hue;
    $[6] = row.market;
    $[7] = row.outcomeImageNsfw;
    $[8] = row.outcomeImageUrl;
    $[9] = row.outcomeIndex;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== color.text) {
    t12 = cn("size-3 rounded-full bg-current", color.text);
    $[11] = color.text;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t12) {
    t13 = <span className={t12} />;
    $[13] = t12;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== row.claimRightsBalanceD6 || $[16] !== row.market || $[17] !== row.wallet || $[18] !== t10 || $[19] !== t11 || $[20] !== t13 || $[21] !== t6 || $[22] !== t8 || $[23] !== t9) {
    t14 = {
      market: t4,
      wallet: t5,
      kind: "prize",
      amountD6: t6,
      burnD6: t7,
      href: t8,
      primaryText: t9,
      secondaryText: t10,
      image: t11,
      imageBadge: t13
    };
    $[15] = row.claimRightsBalanceD6;
    $[16] = row.market;
    $[17] = row.wallet;
    $[18] = t10;
    $[19] = t11;
    $[20] = t13;
    $[21] = t6;
    $[22] = t8;
    $[23] = t9;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const item = t14;
  let t15;
  if ($[25] !== fresh || $[26] !== item || $[27] !== own || $[28] !== view) {
    t15 = own && view === "claimable" ? <ClaimablePrize item={item} fresh={fresh} /> : <RewardListItem item={item} />;
    $[25] = fresh;
    $[26] = item;
    $[27] = own;
    $[28] = view;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
}
export function PortfolioBackerRewardItem(t0) {
  const $ = _c(30);
  const {
    pubkey,
    view,
    wallet,
    fresh,
    isCreator
  } = t0;
  const t = useTranslations("profile.rewards");
  const tMarket = useTranslations("market.info");
  const locale = useLocale();
  const {
    formatD6
  } = useFormattedCurrency();
  const title = useGetBackerRewardsField(pubkey, "marketTitle");
  const image = useGetBackerRewardsField(pubkey, "marketImageUrl");
  const earned = useGetBackerRewardsField(pubkey, "earnedD6");
  const claimed = useGetBackerRewardsField(pubkey, "claimedTotalD6");
  const shares = useGetBackerRewardsField(pubkey, "userShareD6");
  const backed = useGetBackerRewardsField(pubkey, "backedD6");
  const total = useGetBackerRewardsField(pubkey, "backedTotalD6");
  const t1 = (view === "claimable" ? earned : claimed) ?? "0";
  const t2 = shares ?? "0";
  const t3 = `/markets/${pubkey}`;
  const t4 = title ?? pubkey;
  let t5;
  if ($[0] !== isCreator || $[1] !== tMarket) {
    t5 = isCreator ? <><span className="text-foreground-primary">{tMarket("creator")}</span><span aria-hidden="true"> • </span></> : null;
    $[0] = isCreator;
    $[1] = tMarket;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== backed || $[4] !== formatD6 || $[5] !== locale || $[6] !== t || $[7] !== total) {
    t6 = backed !== undefined && total !== undefined ? <span>{t("backingDetails", {
        amount: formatD6(backed),
        share: formatBackerVaultShare(backed, total, locale)
      })}</span> : null;
    $[3] = backed;
    $[4] = formatD6;
    $[5] = locale;
    $[6] = t;
    $[7] = total;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <span>{t5}{t6}</span>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = image ?? null;
  let t9;
  if ($[12] !== pubkey || $[13] !== t8) {
    t9 = <OptimizedImage variant="thumbnail" entity={{
      kind: "market",
      pubkey,
      category: "",
      coverImageUrl: t8,
      nsfw: false
    }} alt="" size="size-12" rounded="rounded-control" preview={false} />;
    $[12] = pubkey;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== pubkey || $[16] !== t1 || $[17] !== t2 || $[18] !== t3 || $[19] !== t4 || $[20] !== t7 || $[21] !== t9 || $[22] !== wallet) {
    t10 = {
      market: pubkey,
      wallet,
      kind: "backer",
      amountD6: t1,
      burnD6: t2,
      href: t3,
      primaryText: t4,
      secondaryText: t7,
      image: t9
    };
    $[15] = pubkey;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t7;
    $[21] = t9;
    $[22] = wallet;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const item = t10;
  let t11;
  if ($[24] !== claimed || $[25] !== fresh || $[26] !== item || $[27] !== view || $[28] !== wallet) {
    t11 = wallet !== null && view === "claimable" ? <ClaimableBackerReward item={item} fresh={fresh} claimedTotalD6={claimed ?? "0"} /> : <RewardListItem item={item} />;
    $[24] = claimed;
    $[25] = fresh;
    $[26] = item;
    $[27] = view;
    $[28] = wallet;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}
function ClaimablePrize(t0) {
  const $ = _c(10);
  const {
    item,
    fresh
  } = t0;
  const claim = useClaimPrize();
  const receipt = useClaimReceipt(item.market, item.wallet ?? undefined);
  const failed = receipt.query.isError;
  let t1;
  if ($[0] !== failed || $[1] !== receipt.query.error?.name) {
    t1 = () => {
      if (failed) {
        logger.warn("portfolio.rewards.claim_receipt_failed", {
          errorName: receipt.query.error?.name
        });
      }
    };
    $[0] = failed;
    $[1] = receipt.query.error?.name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = receipt.query.error?.name;
  let t3;
  if ($[3] !== failed || $[4] !== t2) {
    t3 = [failed, t2];
    $[3] = failed;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t1, t3);
  if (receipt.hasClaimed) {
    return null;
  }
  const t4 = fresh && !receipt.isLoading && !failed;
  let t5;
  if ($[6] !== claim || $[7] !== item || $[8] !== t4) {
    t5 = <ClaimableReward item={item} fresh={t4} claim={claim} />;
    $[6] = claim;
    $[7] = item;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function ClaimableBackerReward(t0) {
  const $ = _c(9);
  const {
    item,
    fresh,
    claimedTotalD6
  } = t0;
  const t1 = item.wallet ?? "";
  let t2;
  if ($[0] !== claimedTotalD6 || $[1] !== item.burnD6 || $[2] !== item.market || $[3] !== t1) {
    t2 = {
      marketAddress: item.market,
      walletAddress: t1,
      userShareD6: item.burnD6,
      claimedTotalD6
    };
    $[0] = claimedTotalD6;
    $[1] = item.burnD6;
    $[2] = item.market;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const claim = useClaimBackerFees(t2);
  if (claim.isAwaitingIndex) {
    return null;
  }
  let t3;
  if ($[5] !== claim || $[6] !== fresh || $[7] !== item) {
    t3 = <ClaimableReward item={item} fresh={fresh} claim={claim} />;
    $[5] = claim;
    $[6] = fresh;
    $[7] = item;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function ClaimableReward(t0) {
  const $ = _c(29);
  const {
    item,
    fresh,
    claim
  } = t0;
  const t = useTranslations("profile.rewards");
  const tClaim = useTranslations("market.claim");
  const {
    activeWallet
  } = useActiveWallet();
  const signingDeps = useTurnkeySigningDeps();
  const confirmed = item.kind === "prize" && claim.data?.ok === true && claim.variables?.marketAddress === item.market && claim.variables?.activeWallet.address === item.wallet;
  if (confirmed) {
    return null;
  }
  const matchesWallet = activeWallet?.address === item.wallet;
  let t1;
  if ($[0] !== claim.isPending || $[1] !== fresh || $[2] !== item.burnD6 || $[3] !== matchesWallet || $[4] !== signingDeps) {
    t1 = !fresh || claim.isPending || !matchesWallet || signingDeps === undefined || !isPositiveRewardAmount(item.burnD6);
    $[0] = claim.isPending;
    $[1] = fresh;
    $[2] = item.burnD6;
    $[3] = matchesWallet;
    $[4] = signingDeps;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const disabled = t1;
  let t2;
  if ($[6] !== activeWallet || $[7] !== claim || $[8] !== disabled || $[9] !== item.burnD6 || $[10] !== item.market || $[11] !== signingDeps) {
    t2 = function claimReward() {
      if (disabled || activeWallet === null || activeWallet === undefined || signingDeps === undefined) {
        return;
      }
      const params = {
        marketAddress: item.market,
        burnAmountD6: item.burnD6,
        activeWallet: {
          id: activeWallet.id,
          address: activeWallet.address,
          type: activeWallet.type
        },
        signingDeps
      };
      claim.mutate(params);
    };
    $[6] = activeWallet;
    $[7] = claim;
    $[8] = disabled;
    $[9] = item.burnD6;
    $[10] = item.market;
    $[11] = signingDeps;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const claimReward = t2;
  let t3;
  if ($[13] !== matchesWallet || $[14] !== t) {
    t3 = matchesWallet ? undefined : t("switchWallet");
    $[13] = matchesWallet;
    $[14] = t;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== claim.isPending || $[17] !== t || $[18] !== tClaim) {
    t4 = claim.isPending ? tClaim("claiming") : t("claim");
    $[16] = claim.isPending;
    $[17] = t;
    $[18] = tClaim;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== claim.isPending || $[21] !== claimReward || $[22] !== disabled || $[23] !== t3 || $[24] !== t4) {
    t5 = <AnimatedButton variant="secondary" size="sm" disabled={disabled} loading={claim.isPending} title={t3} onClick={claimReward}>{t4}</AnimatedButton>;
    $[20] = claim.isPending;
    $[21] = claimReward;
    $[22] = disabled;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== item || $[27] !== t5) {
    t6 = <RewardListItem item={item} action={t5} />;
    $[26] = item;
    $[27] = t5;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  return t6;
}
function RewardListItem(t0) {
  const $ = _c(34);
  const {
    item,
    action
  } = t0;
  const t = useTranslations("profile.rewards");
  const {
    formatD6
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== t) {
    t1 = t("amount");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== formatD6 || $[3] !== item.amountD6) {
    t2 = formatD6(item.amountD6);
    $[2] = formatD6;
    $[3] = item.amountD6;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <span className="text-feedback-success-content">{t2}</span>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = {
      key: "amount",
      label: t1,
      value: t3
    };
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const amountMetric = t4;
  let t5;
  if ($[10] !== item.primaryText) {
    t5 = <span className="truncate">{item.primaryText}</span>;
    $[10] = item.primaryText;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = item.kind === "prize" ? "prize" : "brand";
  let t7;
  if ($[12] !== item.kind || $[13] !== t) {
    t7 = item.kind === "prize" ? t("prize") : t("backer");
    $[12] = item.kind;
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <StatusPill tone={t6} size="xs">{t7}</StatusPill>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t5 || $[19] !== t8) {
    t9 = <span className="flex min-w-0 items-center gap-2">{t5}{t8}</span>;
    $[18] = t5;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== amountMetric) {
    t10 = [amountMetric];
    $[21] = amountMetric;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const t11 = action !== undefined;
  let t12;
  if ($[23] !== action || $[24] !== amountMetric || $[25] !== item.href || $[26] !== item.image || $[27] !== item.imageBadge || $[28] !== item.primaryText || $[29] !== item.secondaryText || $[30] !== t10 || $[31] !== t11 || $[32] !== t9) {
    t12 = <DataListItem testId="portfolio-reward-list-item" href={item.href} ariaLabel={item.primaryText} image={item.image} imageBadge={item.imageBadge} primaryText={t9} secondaryText={item.secondaryText} metrics={t10} compactMetric={amountMetric} action={action} showActionOnMobile={t11} />;
    $[23] = action;
    $[24] = amountMetric;
    $[25] = item.href;
    $[26] = item.image;
    $[27] = item.imageBadge;
    $[28] = item.primaryText;
    $[29] = item.secondaryText;
    $[30] = t10;
    $[31] = t11;
    $[32] = t9;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}